import { Clock, Phone, Truck, QrCode } from "lucide-react";
import { CONTACT, ASSETS } from "../data/menu";

const InfoItem = ({ icon: Icon, label, value, testId, href }) => {
  const content = (
    <>
      <div className="w-11 h-11 rounded-full bg-[#F5EBD3] border border-[#E6DFD3] flex items-center justify-center shrink-0">
        <Icon size={18} strokeWidth={1.5} className="text-[#D96C42]" />
      </div>
      <div>
        <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5146] mb-1">
          {label}
        </div>
        <div className="font-serif text-lg sm:text-xl text-[#2B241D] leading-snug">
          {value}
        </div>
      </div>
    </>
  );
  const baseClasses =
    "flex items-start gap-4 p-5 rounded-2xl border border-[#E6DFD3] bg-[#F4EFE6]/40 hover:bg-[#F5EBD3] transition-colors";
  return href ? (
    <a
      data-testid={testId}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={baseClasses}
    >
      {content}
    </a>
  ) : (
    <div data-testid={testId} className={baseClasses}>
      {content}
    </div>
  );
};

export const Contact = () => {
  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#2B241D] text-[#F5EBD3] overflow-hidden"
    >
      {/* Subtle background image */}
      <div className="absolute inset-0 opacity-[0.08]">
        <img
          src={ASSETS.storefront}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="font-sans text-xs uppercase tracking-[0.3em] text-[#D96C42] mb-6">
              — Visítanos
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium">
              Te esperamos
              <br />
              <em className="italic font-normal text-[#D96C42]">
                con el horno encendido.
              </em>
            </h2>
            <p className="font-sans text-base sm:text-lg text-[#F5EBD3]/70 leading-relaxed font-light mt-8 max-w-md">
              Pide por WhatsApp para ahorrar tiempo, o pásate directamente a
              nuestro local. Te abrimos la puerta y el horno.
            </p>

            <a
              href={CONTACT.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="mt-10 inline-flex items-center gap-3 bg-[#25D366] text-[#0B0B0B] px-8 py-4 rounded-full text-sm font-medium hover:bg-[#1EBE5D] transition-colors"
            >
              Pedir por WhatsApp
            </a>

            {/* Storefront photo */}
            <div className="mt-12 rounded-2xl overflow-hidden border border-[#F5EBD3]/10 shadow-xl shadow-black/30">
              <img
                src={ASSETS.storefront}
                alt="Local de Pizzería Estilo Gali"
                className="w-full h-56 object-cover"
                data-testid="contact-storefront"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <InfoItem
                icon={Clock}
                label="Horario"
                value={CONTACT.hours}
                testId="contact-hours"
              />
              <InfoItem
                icon={Phone}
                label="WhatsApp"
                value={CONTACT.whatsapp}
                testId="contact-phone"
                href={CONTACT.whatsappLink}
              />
              <InfoItem
                icon={Truck}
                label="Envíos a domicilio"
                value={CONTACT.deliveryZone}
                testId="contact-delivery"
              />
              <div
                data-testid="contact-qr"
                className="flex items-start gap-4 p-5 rounded-2xl border border-[#E6DFD3] bg-[#F4EFE6]/40"
              >
                <div className="w-11 h-11 rounded-full bg-[#F5EBD3] border border-[#E6DFD3] flex items-center justify-center shrink-0">
                  <QrCode size={18} strokeWidth={1.5} className="text-[#D96C42]" />
                </div>
                <div>
                  <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5146] mb-1">
                    Ubicación
                  </div>
                  <div className="font-serif text-lg text-[#2B241D] leading-snug">
                    Escanea el QR
                  </div>
                </div>
              </div>
            </div>

            {/* QR code panel */}
            <div className="mt-5 rounded-2xl border border-[#E6DFD3] bg-[#F5EBD3] p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 text-[#2B241D]">
              <div className="w-44 h-44 sm:w-52 sm:h-52 shrink-0 bg-white p-2 rounded-xl border border-[#E6DFD3]">
                <img
                  src={ASSETS.locationQr}
                  alt="Código QR con la ubicación de Pizzería Estilo Gali"
                  className="w-full h-full object-contain"
                  data-testid="location-qr-image"
                />
              </div>
              <div className="text-center sm:text-left">
                <div className="font-sans text-xs uppercase tracking-[0.3em] text-[#D96C42] mb-2">
                  ¿Dónde estamos?
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2B241D] leading-tight">
                  Escanea este código y llega derechito al horno.
                </h3>
                <p className="font-sans text-sm text-[#5C5146] mt-3 font-light leading-relaxed">
                  Apunta la cámara de tu celular hacia el código para abrir la
                  ubicación en tu app de mapas favorita.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
